import React from "react";
import { Button, ButtonTypes } from "./Button";
import { CloseLarge } from "@carbon/icons-react";
import { cn } from "../../utils/styles";

interface ModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  secondButtonClick?: () => void;
  title: string;
  subtitle: string;
  firstButtonLabel?: string;
  secondButtonLabel?: string;
  content?: React.ReactNode;
  titleIcon?: React.ReactNode;
  additionalClassName?: string;
  isSecondButtonDisabled?: boolean;
  firstButtonClick?: () => void;
  firstButtonType?: ButtonTypes;
  secondButtonType?: ButtonTypes;
  contentClassName?: string;
  hideCloseButton?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg";
}

export const Modal = ({
  isModalOpen,
  closeModal,
  secondButtonClick,
  title,
  subtitle,
  firstButtonLabel,
  secondButtonLabel,
  content,
  titleIcon,
  additionalClassName,
  isSecondButtonDisabled,
  firstButtonClick,
  firstButtonType,
  contentClassName,
  secondButtonType,
  hideCloseButton,
}: ModalProps) => {
  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div
        className={cn(
          `bg-white rounded-xl shadow-lg w-full max-w-md sm:max-w-xl border-2 border-white flex flex-col`,
          additionalClassName,
        )}>
        <div className="flex justify-between relative items-center pt-5 pb-4 pl-8 pr-4 bg-gray-100 rounded-t-xl">
          <div className="flex items-center gap-2">
            {titleIcon}
            <h2 className="text-[22px] font-semibold leading-[30.8px] tracking-[0.2px] text-black">
              {title}
            </h2>
          </div>
          {!hideCloseButton && (
            <button
              className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-black cursor-pointer absolute top-3 right-3"
              onClick={closeModal}>
              <CloseLarge size={20} />
            </button>
          )}
        </div>
        <div
          className={cn(
            "pt-6 pb-12 px-8 text-gray-700 max-h-[60vh] overflow-auto no-scrollbar break-words",
            contentClassName,
          )}>
          {content ? (
            content
          ) : (
            <p className="text-[16px] font-light leading-[22.4px] tracking-[0.2px]">{subtitle}</p>
          )}
        </div>
        {(firstButtonLabel || secondButtonLabel) && (
          <div className="flex justify-end space-x-3 p-5 border-t border-gray-200">
            {firstButtonLabel && (
              <Button
                type={firstButtonType ?? "tertiary"}
                label={firstButtonLabel}
                onClick={firstButtonClick ? firstButtonClick : closeModal}
              />
            )}
            {secondButtonLabel && secondButtonClick && (
              <Button
                type={secondButtonType ?? "primary"}
                isDisabled={isSecondButtonDisabled}
                label={secondButtonLabel}
                onClick={secondButtonClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
